import { RoleNames } from '@prospero/shared';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import type { MembershipsResponse } from '../../services/auth/auth.service';
import { switchTenantAtom } from '../../stores/auth.store';
import { authQueryKeys } from './use-memberships';

interface SwitchTenantParams {
  tenantId: string;
  tenantName: string;
}

/**
 * Seed ottimistico della cache memberships: se il tenant target non è ancora
 * in lista (attribuzione arrivata a sessione aperta — flusso "Statistiche
 * clienti" / "I miei clienti"), `ProtectedRoute.decideTenantSelection` letto
 * prima del refetch vedrebbe un context fuori lista e riporterebbe l'utente
 * al tenant precedente, annullando in silenzio lo switch. I chiamanti che
 * passano dal picker hanno sempre il tenant in cache: il seed è un no-op e il
 * ruolo non può che essere Consulente per un cliente attribuito. Il refetch
 * innescato dall'invalidate conferma o smentisce.
 */
export function seedMembershipsCache(queryClient: QueryClient, params: SwitchTenantParams): void {
  const cached = queryClient.getQueryData<MembershipsResponse>(authQueryKeys.memberships());
  if (!cached || cached.memberships.some((m) => m.tenantId === params.tenantId)) {
    return;
  }
  queryClient.setQueryData<MembershipsResponse>(authQueryKeys.memberships(), {
    ...cached,
    memberships: [
      ...cached.memberships,
      {
        tenantId: params.tenantId,
        tenantName: params.tenantName,
        roleId: `seeded-${params.tenantId}`,
        roleName: RoleNames.CONSULTANT,
        // Lo stato abbonamento del cliente qui non si conosce: si semina il
        // valore neutro, cosi' il chip "in prova" non compare mai per finta.
        // Il refetch subito dopo porta il valore vero.
        subscriptionStatus: 'ACTIVE',
        trialEndsAt: null,
        isManaged: false,
        isManagedByMe: false,
        managedBy: null,
        workspaceRelation: null,
      },
    ],
  });
}

export function useSwitchTenant() {
  const setCurrentTenant = useSetAtom(switchTenantAtom);
  const queryClient = useQueryClient();

  const switchTenant = useCallback(
    (params: SwitchTenantParams) => {
      seedMembershipsCache(queryClient, params);
      setCurrentTenant(params);
      // Invalidate ALL queries — the new x-tenant-id will be sent on every refetch.
      queryClient.invalidateQueries();
    },
    [setCurrentTenant, queryClient],
  );

  return { switchTenant };
}

export type { SwitchTenantParams };
